import React, { useState } from 'react';
import { collection, doc, setDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';

export const SendModal = ({ isOpen, onClose, selectedContact, currentAgent }) => {
  const [messageInput, setMessageInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedContact || isSending) return;

    try {
      setIsSending(true);
      setError(null);

      // Generate reference: YYYYMMDD_HHMMSSmmm + phoneno + random 4 chars + "WhatsApp"
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
      const randomChars = Math.random().toString(36).substring(2, 6).toUpperCase();
      const timestamp = `${year}${month}${day}_${hours}${minutes}${seconds}${milliseconds}`;
      const reference = `${timestamp}_${selectedContact}_${randomChars}_WhatsApp`;

      const sendMsgData = {
        msgbody: messageInput.trim(),
        phoneno: selectedContact,
        process_name: 'WhatsApp Chat',
        reference: reference,
        send_code: `${currentAgent?.code || ''}${currentAgent?.phoneno?.replace(/^\+/, '') || ''}`,
        sender: currentAgent?.name || ''
      };

      // Write to x_sendmsg collection using reference as document ID
      const docRef = doc(db, 'x_sendmsg', reference);
      await setDoc(docRef, sendMsgData);

      // Clear input and close modal
      setMessageInput('');
      onClose();

    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg md:max-w-2xl max-h-[90vh] flex flex-col md:my-8 transform transition-all">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
              <span className="text-white text-lg">💬</span>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">New Message</h2>
              <p className="text-sm text-gray-600">To: {selectedContact}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            <span className="text-gray-400 hover:text-gray-600 text-xl">✕</span>
          </button>
        </div>

        {/* Message Input Area */}
        <div className="flex-1 px-6 py-5 space-y-4 overflow-y-auto">
          <div className="relative">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Message Content
            </label>
            <div className="relative">
              <textarea
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                placeholder="Type your message here..."
                className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none transition-all text-gray-900 placeholder-gray-400"
                rows="8"
                disabled={isSending}
                style={{ minHeight: '200px' }}
                onKeyPress={handleKeyPress}
              />
              <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                {messageInput.length}/2000
              </div>
            </div>
            {/* Character count warning */}
            {messageInput.length > 1800 && (
              <div className="mt-2 text-xs text-amber-600 flex items-center">
                <span className="mr-1">⚠️</span>
                Approaching character limit
              </div>
            )}
          </div>

          {/* Quick Templates */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Quick Templates
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { text: "Thank you for your inquiry. We'll get back to you shortly.", label: "Thank You" },
                { text: "Your order has been confirmed and will be delivered soon.", label: "Order Confirmation" },
                { text: "We received your message and are working on it.", label: "Acknowledgment" },
                { text: "Is there anything else I can help you with?", label: "Follow-up" }
              ].map((template, index) => (
                <button
                  key={index}
                  onClick={() => setMessageInput(template.text)}
                  className="px-3 py-2 text-left text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                  disabled={isSending}
                >
                  <div className="font-medium text-gray-700">{template.label}</div>
                  <div className="text-xs text-gray-500 truncate">{template.text}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
              <span className="text-red-500 text-lg">⚠️</span>
              <div>
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                disabled={isSending}
              >
                Cancel
              </button>
              <button
                onClick={() => setMessageInput('')}
                className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                disabled={isSending}
              >
                Clear
              </button>
            </div>

            <button
              onClick={handleSendMessage}
              disabled={!messageInput.trim() || isSending || messageInput.length > 2000}
              className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl hover:from-green-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all transform hover:scale-105 active:scale-95 flex items-center justify-center space-x-2 min-w-[120px]"
            >
              {isSending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <span>📤</span>
                  <span>Send Message</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendModal;
